import { useProducts } from '@hook/useProduct'
import { useUploadFile } from '@hook/useUploadFile'
import { yupResolver } from '@hookform/resolvers/yup'
import { productSchema, REQUIRED_PRODUCT_FIELDS } from '@schema/product.schema'
import { useAppSelector } from '@store/hooks'
import { FormButton, FormStep, Modal } from '@ui'
import { getError, showToast } from '@util'
import { Box, Text } from 'grommet'
import { useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { ProductResMock } from '@/src/types/product.type'
import { PHARMACY_MODAL_TYPE, useModalStore } from '@/src/zustand/Modal'
import { useImagePreviewStore } from '@/src/zustand/store'
import { FaArrowLeft } from 'react-icons/fa'
import ProductSuccess from '../ProductSucess'
import GeneralInfoStep from './GeneralInfoStep'
import PricingStep from './PricingStep'

export type Step = 1 | 2

export type Props = {
  isOpen?: boolean
  onClose: () => void
}

export type ProductFormData = yup.InferType<typeof productSchema>

const CreateProduct = ({ onClose }: Props) => {
  const [step, setStep] = useState<Step>(1)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [lastCreatedProduct, setLastCreatedProduct] = useState<ProductResMock>()
  const { user } = useAppSelector(state => state.user)
  const { imgFile, setImgFile } = useImagePreviewStore()

  const { createProduct } = useProducts()
  const { uploadFile, isUploading } = useUploadFile()
  const { openModal } = useModalStore()
  const methods = useForm({
    defaultValues: {
      cold_chain: false,
      controlled_substance: false,
      generic_name: '',
      is_active: true,
      mark_up_percentage: '',
      vaccine: false,
      vat_percentage: '',
      inventory_unit: ''
    },
    resolver: yupResolver(productSchema)
  })

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = methods

  const [btnType, setBtnType] = useState<'button' | 'submit'>('button')

  useEffect(() => {
    if (step === 2) {
      const timer = setTimeout(() => {
        setBtnType('submit')
      }, 500)

      return () => clearTimeout(timer)
    } else {
      setBtnType('button')
    }
  }, [step])

  const handleBack = () => {
    if (step === 1) {
      reset()
      onClose()
    } else if (step === 2) setStep(1)
  }

  const handleShowSuccess = useCallback(() => {
    setShowSuccessModal(true)
  }, [])

  const isProductFormField = () => {
    const watchValues = methods.getValues()
    // Check required fields
    for (const fieldName of REQUIRED_PRODUCT_FIELDS) {
      const value = watchValues[fieldName as keyof typeof watchValues]
      if (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value?.length === 0)
      ) {
        return false
      }
    }

    // If cold_chain is true, min_temp and max_temp must be present and not empty
    if (watchValues['cold_chain']) {
      if (
        watchValues['min_temp'] === undefined ||
        watchValues['min_temp'] === null ||
        watchValues['min_temp'] === '' ||
        watchValues['max_temp'] === undefined ||
        watchValues['max_temp'] === null ||
        watchValues['max_temp'] === ''
      ) {
        return false
      }
    }

    return true
  }
  const handleStepChange = () => {
    if (step === 1) setStep(2)
    if (!isProductFormField() && step === 2) {
      showToast('Product Fields should be filled first')
      return
    }
  }

  const ModalReset = () => {
    reset()
    setImgFile(null)
  }

  console.log('img_create', methods.watch())

  const onSubmit = async (data: ProductFormData) => {
    let imageSignedId: string | null = null
    if (data.product_image) {
      const uploadResponse = await uploadFile(methods.watch('product_image') as any)
      console.log('img_create', data.product_image)

      imageSignedId = uploadResponse.signed_id
    }
    const {
      vaccine: vaccineValue,
      vaccine_antigen_type,
      vaccine_presentation_type,
      vat_percentage,
      mark_up_percentage,
      ...restAttributes
    } = data

    const payload = {
      data: {
        attributes: {
          ...restAttributes,
          vaccine: vaccineValue,
          ...(imageSignedId && { product_image_signed_id: imageSignedId }),
          ...(vaccineValue && vaccine_antigen_type && { vaccine_antigen_type }),
          ...(vaccineValue && vaccine_presentation_type && { vaccine_presentation_type }),
          mark_up_percentage:
            mark_up_percentage !== undefined && mark_up_percentage !== null ? String(mark_up_percentage) : '',
          vat_percentage: vat_percentage !== undefined || vat_percentage !== null ? String(vat_percentage) : '0'
        },
        relationships: {
          staff: {
            data: {
              id: `${user?.id}`,
              type: 'staff'
            }
          }
        },
        type: 'product'
      }
    }
    try {
      const res = await createProduct(payload)
      showToast('Product created successfully')
      setLastCreatedProduct(res)
      setImgFile(null)

      handleShowSuccess()
      // onClose() is now handled only after/during ProductSuccess modal close
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} reset={ModalReset} title="Create Product">
        <form className="flex flex-col overflow-auto" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-y-4 overflow-auto p-6">
            <Box align="center" className="!min-h-[auto]" direction="row" gap={'16px'} margin={{ bottom: '8px' }}>
              <FormStep check={step > 1} label="General Information" step={1} />
              <Box pad={{ horizontal: '8px' }}>
                <Text color="#ddd">‣</Text>
              </Box>
              <FormStep check={step > 2} label="Pricing" step={2} />
            </Box>
            <Box
              style={{
                minHeight: '320px',
                overflowX: step !== 1 ? 'hidden' : undefined,
                overflowY: step !== 1 ? 'auto' : undefined,
                position: 'relative'
              }}
            >
              <GeneralInfoStep createProd step={step} />
              <PricingStep step={step} />
              {/* <StockStep step={step} /> */}
            </Box>
          </div>
          <FormButton
            cancel={step === 1 ? 'Cancel' : 'Previous'}
            isLoading={isSubmitting || isUploading}
            leftIcon={step === 1 ? undefined : <FaArrowLeft />}
            onClick={() => {
              handleStepChange()
            }}
            onClose={handleBack}
            submit={step === 2 ? 'Save Product' : 'Proceed'}
            type={btnType}
          />
        </form>
      </Modal>
      {showSuccessModal && (
        <ProductSuccess
          description="Product details have been saved. View product details in drug inventory"
          onClick={() => {
            setShowSuccessModal(false)
            if (lastCreatedProduct?.data) {
              console.log({ lastCreatedProduct })
              openModal(PHARMACY_MODAL_TYPE.ADD_STOCK, {
                defaultData: lastCreatedProduct?.data.attributes,
                id: lastCreatedProduct?.data?.id
              })
            }
            onClose()
          }}
          onClose={() => {
            setShowSuccessModal(false)
            onClose()
          }}
          rBtnLabel="Add Stock to this Product"
          title="Drug Added Successfully"
        />
      )}
    </FormProvider>
  )
}

export default CreateProduct
